// What a failed auth request shows (04-errors § 5): the shared presentation decides, the form shows it in place.
// Field errors go under their fields as the catalogue's `field.<code>` text; a field the form has no control for, and
// every other problem with a text, becomes the form's one inline message. Holds and session ends stay central.
import { ApiError } from '@orca/api-client'
import i18next from 'i18next'
import { present } from '../../shared/errors/presentation'
import { errorHooks, routeError } from '../../shared/errors/routeError'

export interface FormProblem<F extends string> {
  fields: Partial<Record<F, string>>
  message?: string
}

/** The catalogue text for a key, falling back from `error.<code>.<reason>` to `error.<code>` when there is no reason text. */
function text(key: string, params?: Record<string, unknown>): string {
  const known = i18next.exists(key) ? key : key.split('.').slice(0, 2).join('.')
  return i18next.t(known, params ?? {})
}

export function fieldText(code: string, params?: Record<string, unknown>): string {
  return text(`field.${code}`, params)
}

export function formProblem<F extends string>(error: unknown, fields: readonly F[]): FormProblem<F> {
  const reaction = present(error)
  switch (reaction.kind) {
    case 'fieldErrors': {
      const shown: Partial<Record<F, string>> = {}
      const rest: string[] = []
      for (const issue of reaction.fields) {
        const line = fieldText(issue.code, issue.params)
        if ((fields as readonly string[]).includes(issue.field)) shown[issue.field as F] ??= line
        else rest.push(line)
      }
      return rest.length > 0 ? { fields: shown, message: rest.join(' ') } : { fields: shown }
    }
    case 'message':
    case 'conflict':
      return { fields: {}, message: text(reaction.key) }
    case 'toast':
      // A refusal that says the access changed refreshes it, so the screen hides what the person can no longer do.
      if (reaction.refreshAccess) errorHooks.refreshAccess()
      return { fields: {}, message: text(reaction.key, reaction.params) }
    case 'offline':
      return { fields: {}, message: text('core.error.offlineWrite') } // a form writes: offline it needs a connection (D193)
    default:
      // A hold, a session end or a re-authentication is the app's, not the form's.
      routeError(error, 'query')
      return { fields: {} }
  }
}

/**
 * A refused link or code (`token_invalid`, 04-errors): the catalogue's sentence and its reason's together — "This link or
 * code doesn’t work anymore. It has expired." — since the reason alone does not say what expired. Anything else: undefined.
 */
export function tokenProblem(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || error.effectiveCode !== 'token_invalid') return undefined
  const what = i18next.t('error.token_invalid')
  const why = error.reason ? `error.token_invalid.${error.reason}` : undefined
  return why && i18next.exists(why) ? `${what}. ${i18next.t(why)}` : what
}
